<template>
    <div class="container mx-auto px-3 pb-32 md:px-8 2xl:max-w-(--breakpoint-xl)" data-test="contacts-list">
        <page-header> Contacts </page-header>

        <contact-list-filters
            v-model="fetchParams"
            :groupId="effectiveGroupId"
            @reset="resetFilters"
            @apply="applyFilters"
        ></contact-list-filters>

        <div v-if="loading && !list.length" class="text-center py-10">
            <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
        </div>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!filtersAreDefault" @reset="resetFilters" />
            <empty-state v-else>
                No contacts found.
                <router-button
                    :to="
                        effectiveGroupId && onCorporate()
                            ? { name: 'contacts.new', query: { group_id: effectiveGroupId } }
                            : { name: 'contacts.new' }
                    "
                >
                    {{ effectiveGroupId ? 'Add' : 'New' }} Contact <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </router-button>
            </empty-state>
        </template>

        <vx-table v-if="list.length" :class="tableClasses" rowBreakpoint="lg">
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Name</div>
                <div>Phone</div>
                <div>Marketing</div>
                <div>Transactional</div>
                <div v-if="FEATURES.contact_groups">Preferred</div>
                <div>Added</div>
            </vx-table-row>

            <contact-row
                v-for="(contact, index) in list"
                :modelValue="list[index]"
                :key="list[index].id"
                :groupId="effectiveGroupId"
                @deleted="removeFromList($event)"
            ></contact-row>
        </vx-table>

        <div v-if="fetchParams.cursor" class="mt-8">
            <vx-button
                @click="loadData()"
                color="muted-dark"
                size="xl"
                class="grow-0"
                data-test="load-more-btn"
                :loading="loading"
            >
                Load More
                <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
            </vx-button>
        </div>
    </div>
</template>
<script setup>
import { faCirclePlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton, VxTable, VxTableRow, VxIconLoading } from '@voxie/frontend-components';
import { onMounted, inject, computed } from 'vue';
import PageHeader from '~/components/general/PageHeader.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import { contactsClient, groupContactsClient } from '~/services';
import ContactListFilters from './ContactListFilters.vue';
import ContactRow from './ContactRow.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useFilterPersistence } from '~/composables/useFilterPersistence';
import { isLargeTeam } from '~/utils/team';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const teamId = Spark.state.currentTeam.id;
const { onCorporate, currentGroup } = useCurrentGroup();

const FEATURES = inject('FEATURES');

const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const props = defineProps({
    groupId: {
        type: String,
        required: false,
    },
});

const effectiveGroupId = computed(() => props.groupId ?? currentGroup.value?.group_id);

let defaultFilters = () => {
    const defaultFilters = {
        'filter[search]': '',
        per_page: 10,
        cursor: null,
        'filter[marketing_subscription_status]': undefined,
        'filter[tag_name]': undefined,
        sort_field: 'id',
        sort_dir: 'desc',
    };

    if (FEATURES.contact_groups && effectiveGroupId.value) {
        defaultFilters['filter[is_preferred]'] = undefined;
    }

    return defaultFilters;
};

const storageKey = effectiveGroupId.value
    ? `contacts-list-filters-group-${effectiveGroupId.value}`
    : 'contacts-list-filters-corporate';

const { isDefault: filtersAreDefault } = useFilterPersistence(storageKey, fetchParams, defaultFilters);

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

onMounted(() => {
    loadData();
});

const removeFromList = (contactId) => {
    list.value = list.value.filter((item) => item.id != contactId);

    if (!list.value.length) {
        loadData();
    }
};

const loadData = () => {
    const params = { ...fetchParams.value };

    if (isLargeTeam(teamId)) {
        if (params['filter[search]'].includes('@')) {
            params['filter[email]'] = params['filter[search]'];
        } else {
            params['filter[phone]'] = params['filter[search]'];
        }
        delete params['filter[search]'];
    }

    if (effectiveGroupId.value) {
        fetchRecords(groupContactsClient.groupContactsList(teamId, effectiveGroupId.value, params));
    } else {
        fetchRecords(contactsClient.searchContacts(teamId, params));
    }
};

const tableClasses = computed(() => {
    return FEATURES.contact_groups ? 'lg:grid-cols-8-auto' : 'lg:grid-cols-7-auto';
});
</script>
